import { Check, Circle } from "lucide-react";
import {
  evaluateStudioPublishReadiness,
  withServerDatasourceReadinessProjection,
} from "@/services/project-api";
import { useStudioStore } from "@/store/studio-store";
import { cn } from "@/lib/utils";
import { useStudioLocale } from "@/i18n/use-studio-locale";
import { usePublicationRelease } from "@/features/publishing/use-publication-release";
import {
  hasVerifiedDeploymentReadback,
  type PublicationReleaseAction,
  type PublicationReleaseProjection,
} from "@/features/publishing/publication-release-types";
import { COOGO_IDE_CHROME_COPY } from "../coogo-ide-chrome-copy";
import type { RailItemId } from "../types";
import { DeploySurface } from "./deploy-surface";
import { EvidenceSurfaceLazy } from "./evidence-surface-lazy";
import { PublishSurface } from "./publish-surface";
import { getReleaseJourneyCopy } from "./release-journey-copy";

type ReleaseStepId = "review" | "prepare" | "launch";

/**
 * C2 — the STEPS, not their words. This array used to carry the English label
 * and description inline, so `ko` and `ja` rendered byte-identical to `en` on
 * the creator's release surface. The producer names the step; the copy module
 * names the language (the PipelineStepper contract, applied here).
 */
const RELEASE_STEPS: readonly {
  id: ReleaseStepId;
  railItem: RailItemId;
}[] = [
  { id: "review", railItem: "evidence" },
  { id: "prepare", railItem: "publish" },
  { id: "launch", railItem: "deploy" },
];

/**
 * The two actions `selectPrimaryAction` offers in `mode="candidate"`
 * (publication-release-panel.tsx). Every other member of
 * `PublicationReleaseAction` is offered by the LAUNCH surface, so this set is
 * written as the complement it is: a new action added to the union defaults
 * to "the launch surface can act", never to a step that closes silently.
 */
const CANDIDATE_ONLY_ACTIONS = new Set<PublicationReleaseAction>([
  "prepare-candidate",
  "run-verification",
]);

/**
 * DEFECT (a), §5.3 of the slice-5B census: `completed` was
 * `activeStep === 'launch'` for Prepare and `activeStep !== 'review'` for
 * Review — so clicking through the stepper drew ✓ on steps that had produced
 * nothing. Captured in one screenshot: Review ✓ Prepare ✓ above a panel
 * reading "No publication candidate".
 *
 * Each step's "done" is now read from the thing that finishes it:
 *
 *  - review   → `evaluateStudioPublishReadiness` (services/project-api). The
 *               Review step IS the editor-preflight surface — evidence-surface
 *               renders that same function's verdict — so the preflight is the
 *               step's own producer, not a borrowed one.
 *  - prepare  → the release projection from
 *               `parsePublicationReleaseProjection`. Prepare's output is a
 *               server-issued candidate bound to the saved revision; a stale
 *               `sourceRevision` is exactly the case where the panel's
 *               `selectPrimaryAction` re-offers "Prepare", so it is not done.
 *  - launch   → `hasVerifiedDeploymentReadback` (publication-release-types),
 *               the same predicate that decides whether the panel is allowed
 *               to show a live URL at all. A deploy nobody has read back is
 *               not a finished launch.
 */
function completedReleaseSteps({
  reviewPassed,
  release,
}: {
  reviewPassed: boolean;
  release: PublicationReleaseProjection | null;
}): Record<ReleaseStepId, boolean> {
  return {
    review: reviewPassed,
    prepare: release !== null && !release.sourceRevision.stale,
    launch: hasVerifiedDeploymentReadback(release),
  };
}

/**
 * DEFECT (b): the Launch step was disabled on `publishGate.publishReady` —
 * the editor preflight estimate, which publish-surface.tsx:42 itself
 * describes as an estimate the release projection overrides. Measured:
 * "Publish readiness ready" with no candidate and no registered target
 * profile, and the Launch step not disabled — the creator admitted to a step
 * whose own surface could not act.
 *
 * The gate now reads `allowedActions`, produced by `filterAllowedActions` in
 * publication-release-types.ts. That is the same set the launch panel checks
 * to enable its own primary button, so the step is reachable exactly when
 * something on it is pressable.
 */
function releaseAllowsLaunch(
  release: PublicationReleaseProjection | null,
): boolean {
  return (release?.allowedActions ?? []).some(
    (action) => !CANDIDATE_ONLY_ACTIONS.has(action),
  );
}

export function EssentialsReleaseSurface() {
  const { locale } = useStudioLocale();
  const copy = COOGO_IDE_CHROME_COPY[locale].releaseStepper;
  const journeyCopy = getReleaseJourneyCopy(locale);
  const activeRailItem = useStudioStore((state) => state.activeRailItem);
  const setActiveRailItem = useStudioStore((state) => state.setActiveRailItem);
  const currentProjectId = useStudioStore((state) => state.currentProjectId);
  const currentProject = useStudioStore((state) => state.currentProject);
  const projectPlan = useStudioStore((state) => state.projectPlan);
  const projectMeta = useStudioStore((state) => state.projectMeta);
  const activeProjectPlan =
    projectPlan ?? projectMeta?.studioProjectPlan ?? null;
  const readinessProject = withServerDatasourceReadinessProjection(
    currentProject,
    projectMeta,
    currentProjectId,
  );
  const publishGate = evaluateStudioPublishReadiness(
    readinessProject as never,
    activeProjectPlan,
    currentProjectId,
  );
  // The same projection the three panels below render. The stepper reports on
  // their work, so it must read their producer and not its own navigation.
  const release = usePublicationRelease(currentProjectId).release;

  const activeStep =
    activeRailItem === "publish"
      ? "prepare"
      : activeRailItem === "deploy"
        ? "launch"
        : "review";

  const completedSteps = completedReleaseSteps({
    reviewPassed: publishGate.publishReady,
    release,
  });
  const launchReachable = releaseAllowsLaunch(release);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-border bg-card px-4 py-3">
        <ol
          aria-label={copy.journey}
          className="mx-auto grid max-w-4xl grid-cols-3 gap-2"
        >
          {RELEASE_STEPS.map((step, index) => {
            const stepCopy = copy.steps[step.id];
            const active = activeStep === step.id;
            const disabled = step.id === "launch" && !launchReachable;
            const completed = completedSteps[step.id];

            return (
              <li key={step.id}>
                <button
                  type="button"
                  disabled={disabled}
                  aria-current={active ? "step" : undefined}
                  data-step-state={
                    disabled
                      ? "unavailable"
                      : completed
                        ? "completed"
                        : "not-completed"
                  }
                  aria-label={
                    disabled
                      ? journeyCopy.launchUnavailable(stepCopy.label)
                      : completed
                        ? journeyCopy.completed(stepCopy.label)
                        : journeyCopy.notCompleted(stepCopy.label)
                  }
                  className={cn(
                    "flex min-h-14 w-full items-start gap-2 rounded-md border px-3 py-2 text-left transition-colors",
                    active
                      ? "border-primary/40 bg-primary/5 text-foreground"
                      : "border-transparent text-muted-foreground hover:bg-muted",
                    disabled && "cursor-not-allowed opacity-45 hover:bg-transparent",
                  )}
                  onClick={() => setActiveRailItem(step.railItem)}
                >
                  <span
                    className={cn(
                      "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold",
                      active || completed
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background",
                    )}
                  >
                    {completed ? (
                      <Check className="h-3 w-3" aria-hidden />
                    ) : active ? (
                      index + 1
                    ) : (
                      <Circle className="h-2.5 w-2.5" aria-hidden />
                    )}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold text-current">
                      {stepCopy.label}
                    </span>
                    <span className="mt-0.5 hidden text-[11px] leading-4 lg:block">
                      {stepCopy.description}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
      <div className="min-h-0 flex-1">
        {activeStep === "launch" ? (
          <DeploySurface />
        ) : activeStep === "prepare" ? (
          <PublishSurface />
        ) : (
          <EvidenceSurfaceLazy />
        )}
      </div>
    </div>
  );
}
